import { FC } from 'react';
import { Card } from '@/components/ui/card';
import { InfoBar } from '@/components/ui/info-bar';
import { StatusBadge } from '@/components/ui/status-badge';
import { DeviceAddressBooks } from '@/types/address-book';
import { AddressBookBlock } from './address-book-block';

type DeviceStatus = 'no-room' | 'no-book' | 'ok';

/**
 * The verification states are mutually exclusive: a device with no books either
 * has no Alexa room (un-provisioned) or a room but no assigned book; otherwise
 * it has its books and is considered OK.
 */
const getDeviceStatus = (device: DeviceAddressBooks): DeviceStatus => {
  if (device.addressBooks.length === 0) {
    return device.roomEndpoint === undefined ? 'no-room' : 'no-book';
  }
  return 'ok';
};

export type DeviceAddressBookCardProps = {
  device: DeviceAddressBooks;
  organisationId: number;
  unitId: number;
  driftDetected: boolean;
  editEnabled: boolean;
};

export const DeviceAddressBookCard: FC<DeviceAddressBookCardProps> = ({
  device,
  organisationId,
  unitId,
  driftDetected,
  editEnabled,
}) => {
  const status = getDeviceStatus(device);
  const showDrift = driftDetected && status === 'ok';

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold">{device.displayName}</h3>
          <span className="text-xs text-gray-500">{device.deviceId}</span>
        </div>
        {showDrift && (
          <StatusBadge text="Address book drift" variant="warning" />
        )}
      </div>

      {status === 'no-room' && (
        <InfoBar text="This device has no Alexa room." variant="error" />
      )}
      {status === 'no-book' && (
        <InfoBar
          text="No address book assigned to this device."
          variant="error"
        />
      )}
      {status === 'ok' &&
        device.addressBooks.map((addressBook) => (
          <AddressBookBlock
            addressBook={addressBook}
            editEnabled={editEnabled}
            key={addressBook.addressBookId}
            organisationId={organisationId}
            unitId={unitId}
          />
        ))}
    </Card>
  );
};
